import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight, FiArrowUpRight, FiMapPin } from 'react-icons/fi';
import PropertyCard from '../components/PropertyCard';
import { apiUrl } from '../config/api';
import { barriosPorDepartamento } from '../data/barriosMendoza';

const zone = barriosPorDepartamento.capital.find((item) => item.name === 'Quinta Sección');
const places = [
  ['Parque General San Martín', 'Naturaleza, deporte y uno de los grandes paisajes urbanos de Mendoza.'],
  ['Avenida Arístides Villanueva', 'Gastronomía y vida social a pocos minutos de las calles residenciales.'],
  ['Paseo Emilio Civit', 'Un corredor histórico que conecta el barrio con el parque y el centro.'],
  ['Universidades y clubes', 'Servicios educativos, culturales y deportivos integrados a la vida cotidiana.'],
];
const demoAds = [
  ['Residencias del Parque', 'Proyecto residencial', 'SUPERDESTACADA', zone.photos[2]],
  ['Casa Patio Emilio Civit', 'Casa', 'DESTACADA', zone.photos[0]],
  ['Arístides Living', 'Departamentos', 'SUPERDESTACADA', zone.photos[0]],
  ['Paseo 5', 'Proyecto boutique', 'DESTACADA', zone.photos[2]],
  ['Terrazas del Oeste', 'Departamentos', 'DESTACADA', zone.photos[1]],
  ['Casa de los Tilos', 'Casa', 'SUPERDESTACADA', zone.photos[2]],
  ['Estudios Quinta', 'Unidades compactas', 'DESTACADA', zone.photos[0]],
  ['Civit 360', 'Proyecto en pozo', 'SUPERDESTACADA', zone.photos[1]],
  ['Parque Norte', 'Departamentos', 'DESTACADA', zone.photos[2]],
  ['La Arboleda', 'Residencias', 'DESTACADA', zone.photos[0]],
];

const trackCampaign = (campaignId, event) => {
  if (!campaignId) return;
  fetch(apiUrl(`/editorial/campaigns/${campaignId}/events`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event }),
    keepalive: true,
  }).catch(() => {});
};

export default function QuintaSeccionEditorial() {
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    let active = true;
    fetch(apiUrl('/editorial/zones/quinta-seccion/campaigns'))
      .then((response) => response.ok ? response.json() : [])
      .then((items) => {
        if (!active) return;
        const available = Array.isArray(items) ? items : [];
        setCampaigns(available);
        available.forEach(({ campaignId }) => {
          const key = `nido-editorial-impression:${campaignId}`;
          if (sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, '1');
          trackCampaign(campaignId, 'impression');
        });
      })
      .catch(() => active && setCampaigns([]));
    return () => { active = false; };
  }, []);

  return (
    <Page>
      <Hero $image={zone.photos[0]}>
        <HeroShade />
        <HeroContent>
          <Kicker>NIDO · GUÍA DE ZONA</Kicker>
          <h1>Quinta<br />Sección.</h1>
          <p>Ciudad, arboleda y parque. Una mirada local para entender cómo se vive y qué oportunidades ofrece uno de los barrios más reconocidos de Mendoza.</p>
          <HeroActions>
            <PrimaryLink to="/buscar?province=mendoza&location=Quinta%20Secci%C3%B3n">
              Ver propiedades en la zona <FiArrowUpRight />
            </PrimaryLink>
            <MapLink href="https://www.google.com/maps/search/?api=1&query=Quinta+Secci%C3%B3n+Mendoza" target="_blank" rel="noreferrer">
              <FiMapPin /> Ubicar en el mapa
            </MapLink>
          </HeroActions>
        </HeroContent>
        <HeroIndex><span>32°53′ S</span><i /><span>Mendoza Capital</span></HeroIndex>
      </Hero>

      <Story>
        <StoryLead>
          <Kicker>IDENTIDAD LOCAL</Kicker>
          <h2>Una forma muy mendocina de vivir la ciudad.</h2>
        </StoryLead>
        <StoryCopy>
          <p>La Quinta Sección combina calles arboladas, vida residencial y cercanía inmediata con el Parque General San Martín. Su ubicación permite moverse con facilidad hacia el centro sin renunciar a una escala barrial.</p>
          <p>Conviven casas tradicionales, edificios contemporáneos, cafés, espacios culturales y servicios cotidianos. Esa mezcla explica por qué continúa siendo una zona buscada tanto para vivir como para invertir.</p>
        </StoryCopy>
      </Story>

      <PhotoEssay>
        <LargePhoto src={zone.photos[2]} alt="Parque y arboleda de Quinta Sección" />
        <PhotoNote>
          <span>01 · PAISAJE</span>
          <h3>El parque como parte de la vida cotidiana.</h3>
          <p>Caminar, entrenar o encontrarse al aire libre forma parte de la identidad del barrio.</p>
        </PhotoNote>
        <SmallPhoto src={zone.photos[1]} alt="Conectividad urbana de Quinta Sección" />
      </PhotoEssay>

      <Places>
        <SectionHeader>
          <div><Kicker>CERCA, TODOS LOS DÍAS</Kicker><h2>Lugares que construyen el barrio.</h2></div>
          <p>Referencias urbanas para comprender el ritmo, las conexiones y la experiencia de vivir en esta zona.</p>
        </SectionHeader>
        <PlaceGrid>
          {places.map(([title, description], index) => (
            <Place key={title}>
              <span>0{index + 1}</span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </Place>
          ))}
        </PlaceGrid>
      </Places>

      <DemoShowcase>
        <SectionHeader>
          <div><Kicker>LABORATORIO COMERCIAL · DEMO</Kicker><h2>Así puede verse la visibilidad paga.</h2></div>
          <p>Diez simulaciones para probar jerarquías y presencia visual. No son publicaciones reales ni promociones contratadas.</p>
        </SectionHeader>
        <DemoLegend>
          <span><LegendDot $super /> Súper destacada: máxima presencia editorial</span>
          <span><LegendDot /> Destacada: prioridad dentro de la selección</span>
        </DemoLegend>
        <DemoGrid>
          {demoAds.map(([title, type, tier, image], index) => (
            <DemoCard key={title} $super={tier === 'SUPERDESTACADA'}>
              <DemoImage><img src={image} alt="" /><DemoBadge $super={tier === 'SUPERDESTACADA'}>{tier === 'SUPERDESTACADA' ? 'SÚPER DESTACADA' : 'DESTACADA'}</DemoBadge><DemoFlag>SIMULACIÓN</DemoFlag></DemoImage>
              <DemoBody>
                <span>{type} · Quinta Sección</span>
                <h3>{title}</h3>
                <footer><small>Vista comercial 0{index + 1}</small><FiArrowUpRight /></footer>
              </DemoBody>
            </DemoCard>
          ))}
        </DemoGrid>
      </DemoShowcase>

      <Commercial>
        <SectionHeader>
          <div><Kicker>OPORTUNIDADES EN LA ZONA</Kicker><h2>Propiedades con mayor visibilidad.</h2></div>
          <p>Una selección de avisos promocionados que coinciden con Quinta Sección. El contenido comercial siempre se identifica de manera transparente.</p>
        </SectionHeader>
        {campaigns.length > 0 ? (
          <PropertyGrid>{campaigns.map(({ campaignId, property }) => (
            <CampaignCard key={campaignId} onClick={() => trackCampaign(campaignId, 'click')}>
              <PropertyCard property={{ ...property, editorialCampaignId: campaignId }} />
            </CampaignCard>
          ))}</PropertyGrid>
        ) : (
          <CommercialEmpty>
            <span>ESPACIO COMERCIAL DISPONIBLE</span>
            <h3>Todavía no hay propiedades promocionadas en esta guía.</h3>
            <p>Las publicaciones aparecerán aquí únicamente cuando tengan un destaque activo y pertenezcan a Quinta Sección.</p>
            <Link to="/client/planes">Conocer opciones de visibilidad <FiArrowUpRight /></Link>
          </CommercialEmpty>
        )}
      </Commercial>

      <Explore>
        <div><Kicker>INVENTARIO COMPLETO</Kicker><h2>Seguí explorando Quinta Sección.</h2><p>Accedé a todas las propiedades disponibles y ajustá operación, tipo, precio y características desde el buscador.</p></div>
        <PrimaryLink to="/buscar?province=mendoza&location=Quinta%20Secci%C3%B3n">
          Ver todas las propiedades <FiArrowRight />
        </PrimaryLink>
      </Explore>
    </Page>
  );
}

const Page = styled.main`background:#f7f5ef;color:#29474f;`;
const CampaignCard = styled.div`min-width:0;`;
const Hero = styled.header`position:relative;min-height:min(780px,88vh);display:flex;align-items:flex-end;overflow:hidden;background:url('${({$image})=>$image}') center/cover;`;
const HeroShade = styled.div`position:absolute;inset:0;background:linear-gradient(90deg,rgba(23,46,52,.9),rgba(23,46,52,.22) 68%),linear-gradient(0deg,rgba(23,46,52,.5),transparent 55%);`;
const HeroContent = styled.div`position:relative;width:min(1180px,calc(100% - 40px));margin:0 auto;padding:90px 0;color:#fff;h1{margin:16px 0 25px;font-size:clamp(70px,11vw,150px);font-weight:520;line-height:.76;letter-spacing:-.075em;}p{max-width:680px;color:rgba(255,255,255,.8);font-size:clamp(16px,1.7vw,20px);line-height:1.65;}@media(max-width:600px){padding:70px 0 54px;h1{font-size:70px;}}`;
const Kicker = styled.span`display:block;color:#d98968;font-size:12px;font-weight:800;letter-spacing:.18em;`;
const HeroActions = styled.div`display:flex;align-items:center;gap:15px;margin-top:33px;@media(max-width:650px){align-items:flex-start;flex-direction:column;gap:10px;}`;
const PrimaryLink = styled(Link)`display:inline-flex;width:fit-content;max-width:100%;min-height:54px;align-items:center;justify-content:center;gap:13px;padding:0 23px;border-radius:999px;background:#c96f4c;color:#fff;text-decoration:none;font-size:15px;font-weight:800;box-shadow:0 12px 30px rgba(75,34,20,.18);box-sizing:border-box;`;
const MapLink = styled.a`display:inline-flex;width:fit-content;max-width:100%;min-height:54px;align-items:center;gap:10px;padding:0 21px;border:1px solid rgba(255,255,255,.4);border-radius:999px;color:#fff;text-decoration:none;font-weight:700;backdrop-filter:blur(10px);box-sizing:border-box;`;
const HeroIndex = styled.div`position:absolute;right:35px;bottom:35px;display:flex;align-items:center;gap:12px;color:rgba(255,255,255,.7);font-size:12px;letter-spacing:.08em;i{width:35px;height:1px;background:currentColor;}@media(max-width:700px){display:none;}`;
const Story = styled.section`display:grid;grid-template-columns:1.05fr .95fr;gap:clamp(45px,9vw,130px);max-width:1180px;margin:auto;padding:120px 30px;@media(max-width:760px){grid-template-columns:1fr;padding:78px 22px;}`;
const StoryLead = styled.div`h2{max-width:600px;margin:17px 0 0;font-size:clamp(43px,6vw,78px);font-weight:520;line-height:.94;letter-spacing:-.06em;}`;
const StoryCopy = styled.div`padding-top:25px;p{margin:0 0 23px;color:#68777b;font-size:17px;line-height:1.8;}`;
const PhotoEssay = styled.section`display:grid;grid-template-columns:1.35fr .65fr;grid-template-rows:270px 310px;gap:18px;max-width:1380px;margin:auto;padding:0 30px 120px;img{width:100%;height:100%;object-fit:cover;border-radius:22px;}@media(max-width:700px){grid-template-columns:1fr;grid-template-rows:360px auto 260px;padding:0 15px 80px;}`;
const LargePhoto = styled.img`grid-row:1/3;`;
const SmallPhoto = styled.img``;
const PhotoNote = styled.div`display:flex;flex-direction:column;justify-content:flex-end;padding:30px;border-radius:22px;background:#294f58;color:#fff;span{color:#e6a086;font-size:12px;font-weight:800;letter-spacing:.16em;}h3{margin:14px 0 9px;font-size:27px;line-height:1.05;}p{margin:0;color:#c9d5d4;line-height:1.55;}`;
const Places = styled.section`padding:110px max(25px,calc((100vw - 1180px)/2));background:#fff;`;
const SectionHeader = styled.header`display:flex;align-items:flex-end;justify-content:space-between;gap:50px;margin-bottom:50px;h2{max-width:720px;margin:15px 0 0;font-size:clamp(40px,5vw,68px);font-weight:520;line-height:.94;letter-spacing:-.055em;}p{max-width:430px;margin:0;color:#718083;line-height:1.65;}@media(max-width:760px){align-items:flex-start;flex-direction:column;gap:22px;}`;
const PlaceGrid = styled.div`display:grid;grid-template-columns:repeat(2,1fr);border-top:1px solid rgba(41,71,79,.14);@media(max-width:700px){grid-template-columns:1fr;}`;
const Place = styled.article`display:grid;grid-template-columns:55px 1fr;gap:15px;padding:30px 20px 30px 0;border-bottom:1px solid rgba(41,71,79,.14);&:nth-child(odd){border-right:1px solid rgba(41,71,79,.14);}@media(max-width:700px){border-right:0!important;}span{color:#c66f4e;font-size:12px;font-weight:800;}h3{margin:0 0 8px;font-size:23px;}p{margin:0;color:#718083;line-height:1.55;}`;
const DemoShowcase = styled.section`padding:115px max(25px,calc((100vw - 1240px)/2));background:#eef1ed;`;
const DemoLegend = styled.div`display:flex;flex-wrap:wrap;gap:14px 28px;margin:-20px 0 38px;color:#66777a;font-size:12px;font-weight:700;span{display:flex;align-items:center;gap:9px;}`;
const LegendDot = styled.i`width:10px;height:10px;border-radius:50%;background:${({$super})=>$super?'#294f58':'#c66f4e'};box-shadow:${({$super})=>$super?'0 0 0 4px rgba(41,79,88,.12)':'none'};`;
const DemoGrid = styled.div`display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:16px;@media(max-width:1100px){grid-template-columns:repeat(3,1fr);}@media(max-width:750px){grid-template-columns:repeat(2,1fr);}@media(max-width:470px){grid-template-columns:1fr;}`;
const DemoCard = styled.article`overflow:hidden;border:${({$super})=>$super?'2px solid #294f58':'1px solid rgba(41,71,79,.13)'};border-radius:20px;background:#fff;box-shadow:${({$super})=>$super?'0 22px 50px rgba(41,79,88,.16)':'0 12px 30px rgba(41,79,88,.06)'};transform:${({$super})=>$super?'translateY(-5px)':'none'};`;
const DemoImage = styled.div`position:relative;height:185px;overflow:hidden;img{width:100%;height:100%;object-fit:cover;}`;
const DemoBadge = styled.span`position:absolute;left:12px;bottom:12px;padding:8px 10px;border-radius:999px;background:${({$super})=>$super?'#294f58':'#c66f4e'};color:#fff;font-size:12px;font-weight:850;letter-spacing:.11em;`;
const DemoFlag = styled.span`position:absolute;top:11px;right:11px;padding:6px 8px;border-radius:999px;background:rgba(255,255,255,.9);color:#294f58;font-size:12px;font-weight:850;letter-spacing:.1em;`;
const DemoBody = styled.div`padding:18px;>span{color:#b45f40;font-size:12px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;}h3{min-height:45px;margin:9px 0 18px;font-size:20px;line-height:1.05;}footer{display:flex;align-items:center;justify-content:space-between;padding-top:13px;border-top:1px solid rgba(41,71,79,.1);small{color:#7b898b;font-size:12px;}svg{color:#c66f4e;}}`;
const Commercial = styled.section`max-width:1240px;margin:auto;padding:120px 30px;`;
const PropertyGrid = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:22px;@media(max-width:980px){grid-template-columns:repeat(2,1fr);}@media(max-width:650px){grid-template-columns:1fr;}`;
const CommercialEmpty = styled.div`padding:48px;border:1px solid rgba(41,71,79,.13);border-radius:24px;background:rgba(255,255,255,.7);span{color:#c66f4e;font-size:12px;font-weight:800;letter-spacing:.16em;}h3{max-width:700px;margin:14px 0;font-size:32px;}p{max-width:680px;color:#718083;line-height:1.6;}a{display:inline-flex;align-items:center;gap:9px;margin-top:15px;color:#29474f;font-weight:800;}`;
const Explore = styled.section`display:flex;align-items:flex-end;justify-content:space-between;gap:50px;padding:100px max(25px,calc((100vw - 1180px)/2));background:#294f58;color:#fff;h2{max-width:760px;margin:14px 0;font-size:clamp(45px,6vw,80px);font-weight:520;line-height:.92;letter-spacing:-.06em;}p{max-width:650px;color:#c8d6d5;line-height:1.65;}@media(max-width:800px){align-items:flex-start;flex-direction:column;}`;
